import React, { useState, useMemo } from 'react';
import MathText from '../../VueQuestion/MathText';
import { attendusDeRubrique } from '../../../utils/rubriqueAttendus';
import { X, Calendar, Clock, Palette, BookOpen, Target, Plus, ChevronDown, Check, Sparkles, AlertCircle, Search, AlertTriangle, Lightbulb } from 'lucide-react';

// Confirmation avant CHANGEMENT DE THÈME quand une sélection serait perdue
// (même esthétique que ConfirmDeleteModal de RoadMapNiveau : header dégradé
// animé + note positive : une séquence porte UN thème, mais des activités
// d'autres thèmes pourront s'y greffer après création).
const ConfirmChangeThemeModal = ({ ancienTheme, nouveauTheme, nbSelections, onConfirm, onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-md overflow-hidden">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-amber-500" />
        <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 100" preserveAspectRatio="none">
          <path fill="white">
            <animate attributeName="d" dur="4s" repeatCount="indefinite"
              values="M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z;
                      M0,50 Q100,80 200,50 Q300,20 400,50 L400,100 L0,100 Z;
                      M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z" />
          </path>
        </svg>
        <div className="relative px-3 sm:px-6 py-3 sm:py-5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-2 sm:p-3 bg-white/20 backdrop-blur rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-xl font-bold text-white truncate">Changer de thème ?</h2>
              <p className="text-white/80 text-xs sm:text-sm">Votre sélection actuelle sera remplacée</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 sm:p-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-colors flex-shrink-0">
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </button>
        </div>
      </div>
      <div className="p-3 sm:p-6">
        <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
          Une séquence est construite sur <strong>un seul thème</strong>. En passant
          de « {ancienTheme} » à « {nouveauTheme} », votre sélection sera effacée :
        </p>
        <div className="p-3 sm:p-4 bg-orange-50 border-2 border-orange-200 rounded-lg sm:rounded-xl mb-3 sm:mb-4">
          <p className="font-bold text-orange-800 text-sm sm:text-base">
            {nbSelections} élément{nbSelections > 1 ? 's' : ''} sélectionné{nbSelections > 1 ? 's' : ''} (savoirs-faires et attendus)
          </p>
        </div>
        <div className="flex items-start gap-2 p-3 sm:p-4 bg-sky-50 border border-sky-200 rounded-lg sm:rounded-xl mb-4 sm:mb-6">
          <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-sky-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-sky-800">
            Une fois la séquence créée, vous pourrez y ajouter des <strong>activités
            portant sur d'autres thèmes</strong> : pas besoin de tout faire tenir ici.
          </p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 sm:px-5 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl font-medium hover:bg-gray-200 transition-colors text-sm sm:text-base"
          >
            Garder ma sélection
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg sm:rounded-xl font-medium
              hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-500/30
              hover:shadow-xl hover:shadow-orange-500/40 text-sm sm:text-base"
          >
            Changer de thème
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Composant pour une option de savoir/attendu
const SavoirOption = ({ label, onChange, checked, type }) => {
  const renderTextWithLineBreaks = (text) => {
    if (!text) return '';
    return text.split('<br>').map((line, index, array) => (
      <React.Fragment key={index}>
        <MathText>{line}</MathText>
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <label className={`
      flex items-start gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 p-3 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-xl cursor-pointer transition-all duration-200
      ${checked
        ? type === 'savoir'
          ? 'bg-blue-100 border-2 border-blue-400 shadow-md'
          : 'bg-emerald-100 border-2 border-emerald-400 shadow-md'
        : 'bg-white border-2 border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }
    `}>
      <div className={`
        w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all
        ${checked
          ? type === 'savoir'
            ? 'bg-blue-500 border-blue-500'
            : 'bg-emerald-500 border-emerald-500'
          : 'border-gray-300 bg-white'
        }
      `}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="hidden"
      />
      <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-700 leading-relaxed">
        {renderTextWithLineBreaks(label)}
      </span>
    </label>
  );
};

function AddSequenceNiveau({ isOpen, onClose, themes, niveau, onSave, isLoading = false, dejaAbordes = null }) {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedSavoirs, setSelectedSavoirs] = useState([]);
  const [selectedAttendus, setSelectedAttendus] = useState([]);
  const [activeTab, setActiveTab] = useState('attendu');
  const [expandedDescripteur, setExpandedDescripteur] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSavoirsSection, setShowSavoirsSection] = useState(false);

  // Trouver le thème sélectionné pour accéder aux descripteurs
  const currentTheme = useMemo(() => {
    if (!themes || !selectedTheme) return null;
    return themes.find(t => t.nom === selectedTheme);
  }, [themes, selectedTheme]);


  // Descripteurs du thème sélectionné. Une SÉQUENCE ne porte que des
  // OBJECTIFS d'apprentissage (décision 2026-08-18) : les automatismes se
  // travaillent en activités rituelles, ils sont filtrés à la source — le
  // rendu, la recherche et les compteurs suivent d'eux-mêmes.
  const descripteursSource = useMemo(() => {
    return (currentTheme?.descripteur || []).map((desc) => ({
      ...desc,
      attendus: attendusDeRubrique(desc.attendus, 'objectif'),
    }));
  }, [currentTheme]);

  // Filtre « uniquement les attendus restants » (demande prof 2026-08-25),
  // ACTIF par défaut : à la création d'une séquence, on ne montre que les
  // attendus encore jamais abordés dans la progression — le prof sait ce
  // qui lui reste à placer. Les attendus cochés ici ne sont jamais
  // masqués, et l'interrupteur réaffiche tout (retravail possible).
  const [seulementRestants, setSeulementRestants] = useState(true);
  const selectedAttenduIds = useMemo(
    () => new Set(selectedAttendus.map((a) => a.id)),
    [selectedAttendus]
  );
  const descripteurs = useMemo(() => {
    if (!dejaAbordes || !seulementRestants) return descripteursSource;
    return descripteursSource.map((desc) => ({
      ...desc,
      attendus: (desc.attendus || []).filter(
        (a) => !dejaAbordes.has(a.id) || selectedAttenduIds.has(a.id)
      ),
    }));
  }, [descripteursSource, dejaAbordes, seulementRestants, selectedAttenduIds]);
  const nbMasques = useMemo(() => {
    const compte = (liste) => liste.reduce((s, d) => s + (d.attendus || []).length, 0);
    return compte(descripteursSource) - compte(descripteurs);
  }, [descripteursSource, descripteurs]);

    
  // Vérifier si le thème a des savoirs-faires
  const hasSavoirsFaires = useMemo(() => {
    return descripteurs.some(desc => desc.savoirs_faires && desc.savoirs_faires.length > 0);
  }, [descripteurs]);

  // Résultats de recherche
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const results = { savoirs: [], attendus: [] };

    descripteurs.forEach(desc => {
      (desc.savoirs_faires || []).forEach(s => {
        if ((s.nom || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.savoirs.push({ ...s, descripteurNom: desc.nom });
        }
      });

      (desc.attendus || []).forEach(a => {
        if ((a.description || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.attendus.push({ ...a, descripteurNom: desc.nom });
        }
      });
    });

    return results;
  }, [descripteurs, searchQuery]);

  // Calculer la durée en semaines
  const calculateDuration = () => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.ceil(diffDays / 7);
    return `${weeks} semaine${weeks > 1 ? 's' : ''}`;
  };

  // Validation des erreurs
  const validationErrors = useMemo(() => {
    const errors = [];

    if (!title.trim()) {
      errors.push("Le titre de la séquence est obligatoire");
    }

    if (!selectedTheme) {
      errors.push("Veuillez sélectionner un thème");
    }

    if (!startDate) {
      errors.push("La date de début est obligatoire");
    }

    if (!endDate) {
      errors.push("La date de fin est obligatoire");
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      errors.push("La date de fin doit être postérieure à la date de début");
    }

    return errors;
  }, [title, selectedTheme, startDate, endDate]);

  const isFormValid = validationErrors.length === 0;

  const handleSavoirChange = (savoir) => {
    setSelectedSavoirs(prev => {
      const exists = prev.some(s => s.id === savoir.id);
      if (exists) {
        return prev.filter(s => s.id !== savoir.id);
      }
      return [...prev, savoir];
    });
  };

  const handleAttenduChange = (attendu) => {
    setSelectedAttendus(prev => {
      const exists = prev.some(a => a.id === attendu.id);
      if (exists) {
        return prev.filter(a => a.id !== attendu.id);
      }
      return [...prev, attendu];
    });
  };

  const handleSave = () => {
    const selectedThemeObj = themes?.find(t => t.nom === selectedTheme);

    onSave({
      title,
      start: startDate,
      end: endDate,
      theme: selectedThemeObj?.id || null,
      niveau: niveau,
      savoir_faires: selectedSavoirs.map(sf => sf.id),
      attendus: selectedAttendus.map(att => att.id),
      est_commune: true
    });
  };

  const handleClose = () => {
    // Réinitialiser tous les champs
    setTitle('');
    setStartDate('');
    setEndDate('');
    setSelectedTheme('');
    setSelectedSavoirs([]);
    setSelectedAttendus([]);
    setSearchQuery('');
    setShowSavoirsSection(false);
    setExpandedDescripteur(null);
    setThemeEnAttente(null);
    onClose();
  };

  // Thème en attente de confirmation (le changement écrase la sélection)
  const [themeEnAttente, setThemeEnAttente] = useState(null);

  // Appliquer réellement le changement : reset savoirs/attendus
  const appliquerTheme = (themeName) => {
    setSelectedTheme(themeName);
    setSelectedSavoirs([]);
    setSelectedAttendus([]);
    setExpandedDescripteur(null);
    setSearchQuery('');
    setThemeEnAttente(null);
  };

  // Changer de thème écrase la sélection → confirmation AVANT, mais
  // uniquement s'il y a quelque chose à perdre (sinon changement direct).
  const handleThemeChange = (themeName) => {
    const aPerdre = selectedSavoirs.length + selectedAttendus.length;
    if (selectedTheme && themeName !== selectedTheme && aPerdre > 0) {
      setThemeEnAttente(themeName);
      return;
    }
    appliquerTheme(themeName);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-blue-500/20 w-full max-w-3xl max-h-[90vh] overflow-hidden border border-white/50 flex flex-col">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-base font-semibold text-slate-800">Création en cours...</p>
            <p className="text-sm text-slate-500 mt-1">Veuillez patienter</p>
          </div>
        )}

        {/* Header avec glassmorphism */}
        <div className="relative overflow-hidden flex-shrink-0">
          {/* Glow effect */}
          <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 rounded-3xl blur-2xl opacity-30 animate-pulse" />

          <div className="relative overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-sky-500 to-cyan-400" />

            {/* Animated blobs */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
              <defs>
                <linearGradient id="add-blob1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#c4b5fd" stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="add-blob2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.1" />
                </linearGradient>
              </defs>

              <path fill="url(#add-blob1)">
                <animate attributeName="d" dur="8s" repeatCount="indefinite"
                  values="M0,100 Q200,50 400,80 Q600,120 800,70 L800,200 L0,200 Z;
                          M0,70 Q200,110 400,60 Q600,80 800,120 L800,200 L0,200 Z;
                          M0,100 Q200,50 400,80 Q600,120 800,70 L800,200 L0,200 Z" />
              </path>

              <path fill="url(#add-blob2)">
                <animate attributeName="d" dur="6s" repeatCount="indefinite"
                  values="M0,130 Q300,90 500,110 Q700,140 800,95 L800,200 L0,200 Z;
                          M0,95 Q300,130 500,90 Q700,105 800,140 L800,200 L0,200 Z;
                          M0,130 Q300,90 500,110 Q700,140 800,95 L800,200 L0,200 Z" />
              </path>

              <circle cx="150" cy="80" r="2" fill="white" opacity="0.4">
                <animate attributeName="cy" dur="3s" values="80;50;80" repeatCount="indefinite" />
              </circle>
              <circle cx="650" cy="100" r="2" fill="white" opacity="0.3">
                <animate attributeName="cy" dur="4s" values="100;65;100" repeatCount="indefinite" />
              </circle>
            </svg>

            {/* Header content */}
            <div className="relative px-6 xl:px-4 2xl:px-5 3xl:px-6 py-5 xl:py-3 2xl:py-4 3xl:py-5 flex items-center justify-between">
              <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-white/20 backdrop-blur rounded-xl border border-white/30 shadow-xl">
                  <Plus className="w-6 h-6 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl xl:text-sm 2xl:text-base 3xl:text-xl font-bold text-white">
                    Nouvelle séquence
                  </h2>
                  <p className="text-white/80 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                    Créer une séquence pour le niveau {niveau}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 bg-orange-500/80 hover:bg-orange-600 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 xl:p-3 2xl:p-4 3xl:p-6 overflow-y-auto flex-1 min-h-0">
          {/* Titre */}
          <div className="mb-5 xl:mb-3 2xl:mb-4 3xl:mb-5">
            <label className="block text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-slate-700 mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2">
              Titre de la séquence *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 xl:px-3 2xl:px-3 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 border-gray-200 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-800
                focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                transition-all duration-300 bg-white/80"
              placeholder="Ex: Nombres relatifs"
            />
          </div>

          {/* Thème */}
          <div className="mb-5 xl:mb-3 2xl:mb-4 3xl:mb-5">
            <label className=" text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-slate-700 mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2 flex items-center gap-2">
              <Palette className="w-4 h-4 text-purple-500" />
              Thème *
            </label>
            <select
              value={selectedTheme}
              onChange={(e) => handleThemeChange(e.target.value)}
              className="w-full px-4 xl:px-3 2xl:px-3 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 border-gray-200 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-800
                focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                transition-all duration-300 bg-white/80 cursor-pointer"
            >
              <option value="">Sélectionner un thème</option>
              {themes && themes.map((theme) => (
                <option key={theme.id} value={theme.nom}>
                  {theme.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 xl:gap-2 2xl:gap-3 3xl:gap-4 mb-5 xl:mb-3 2xl:mb-4 3xl:mb-5">
            <div>
              <label className=" text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-slate-700 mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Date de début *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 xl:px-3 2xl:px-3 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 border-gray-200 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-800
                  focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                  transition-all duration-300 bg-white/80"
              />
            </div>
            <div>
              <label className=" text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-semibold text-slate-700 mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Date de fin *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-4 xl:px-3 2xl:px-3 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 border-gray-200 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-800
                  focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                  transition-all duration-300 bg-white/80"
              />
            </div>
          </div>

          {/* Durée calculée */}
          {startDate && endDate && (
            <div className="mb-6 xl:mb-3 2xl:mb-4 3xl:mb-6 p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-500" />
              <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-purple-700">
                Durée estimée : {calculateDuration()}
              </span>
            </div>
          )}

          {/* Section Savoirs/Attendus (collapsible) */}
          {selectedTheme && (
            <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
              {/* Header section savoirs */}
              <button
                onClick={() => setShowSavoirsSection(!showSavoirsSection)}
                className="w-full flex items-center justify-between p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-gradient-to-r from-gray-50 to-slate-50 hover:from-blue-50 hover:to-sky-50 transition-colors"
              >
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-blue-100 rounded-lg">
                    <Sparkles className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-bold text-slate-800">
                      Savoirs & Attendus
                    </h3>
                    <p className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-slate-500">
                      {selectedSavoirs.length + selectedAttendus.length} élément(s) sélectionné(s)
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${showSavoirsSection ? 'rotate-180' : ''}`} />
              </button>

              {/* Contenu section savoirs */}
              {showSavoirsSection && (
                <div className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 border-t border-gray-200 bg-white">
                  {/* Tabs */}
                  <div className="flex items-center justify-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
                    {hasSavoirsFaires && (
                      <button
                        onClick={() => setActiveTab('savoir')}
                        className={`
                          flex items-center gap-2 px-4 xl:px-3 2xl:px-3 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-xl font-medium text-sm xl:text-xs 2xl:text-xs 3xl:text-sm transition-all
                          ${activeTab === 'savoir'
                            ? 'bg-blue-500 text-white shadow-lg'
                            : 'bg-white/50 text-slate-600 hover:bg-white/80'
                          }
                        `}
                      >
                        <BookOpen className="w-4 h-4" />
                        Savoirs-faires
                      </button>
                    )}
                    <button
                      onClick={() => setActiveTab('attendu')}
                      className={`
                        flex items-center gap-2 px-4 xl:px-3 2xl:px-3 3xl:px-4 py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-xl font-medium text-sm xl:text-xs 2xl:text-xs 3xl:text-sm transition-all
                        ${activeTab === 'attendu'
                          ? 'bg-emerald-500 text-white shadow-lg'
                          : 'bg-white/50 text-slate-600 hover:bg-white/80'
                        }
                      `}
                    >
                      <Target className="w-4 h-4" />
                      Attendus
                    </button>
                  </div>

                  {/* Filtre « attendus restants non enseignés ailleurs » */}
                  {activeTab === 'attendu' && dejaAbordes ? (
                    <div className="mb-3 flex items-center flex-wrap gap-2">
                      <button
                        onClick={() => setSeulementRestants((v) => !v)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all ${
                          seulementRestants
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border-2 ${
                          seulementRestants ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                        }`}>
                          {seulementRestants ? <Check className="w-2.5 h-2.5 text-white" /> : null}
                        </span>
                        Uniquement les attendus restants
                      </button>
                      {seulementRestants && nbMasques > 0 ? (
                        <span className="text-[11px] font-semibold text-gray-400">
                          {nbMasques} déjà abordé{nbMasques > 1 ? 's' : ''} masqué{nbMasques > 1 ? 's' : ''}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  {/* Champ de recherche */}
                  <div className="mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
                    <div className="relative">
                      <Search className="absolute left-4 xl:left-3 top-1/2 -translate-y-1/2 w-5 h-5 xl:w-4 xl:h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={activeTab === 'savoir' ? "Rechercher un savoir-faire..." : "Rechercher un attendu..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 xl:pl-10 pr-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 border-gray-200 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                          focus:border-blue-400 focus:ring-2 text-black focus:ring-blue-100 focus:outline-none
                          transition-all duration-300 bg-white/80"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-200 rounded-full
                            flex items-center justify-center hover:bg-gray-300 transition-colors"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Liste des résultats ou descripteurs */}
                  <div className="max-h-60 overflow-y-auto space-y-3">
                    {searchResults ? (
                      // Résultats de recherche
                      <>
                        {activeTab === 'savoir' && (
                          searchResults.savoirs.length > 0 ? (
                            <>
                              <p className="text-xs text-gray-500 mb-2">
                                {searchResults.savoirs.length} savoir-faire(s) trouvé(s)
                              </p>
                              {searchResults.savoirs.map((savoir) => (
                                <div key={savoir.id}>
                                  <span className="inline-block mb-1 ml-3 px-2 py-0.5 bg-blue-100 text-blue-600 text-[10px] rounded-full">
                                    {savoir.descripteurNom}
                                  </span>
                                  <SavoirOption
                                    label={savoir.nom}
                                    onChange={() => handleSavoirChange(savoir)}
                                    checked={selectedSavoirs.some(s => s.id === savoir.id)}
                                    type="savoir"
                                  />
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-gray-500 text-sm">Aucun savoir-faire trouvé</p>
                            </div>
                          )
                        )}

                        {activeTab === 'attendu' && (
                          searchResults.attendus.length > 0 ? (
                            <>
                              <p className="text-xs text-gray-500 mb-2">
                                {searchResults.attendus.length} attendu(s) trouvé(s)
                              </p>
                              {searchResults.attendus.map((attendu) => (
                                <div key={attendu.id}>
                                  <span className="inline-block mb-1 ml-3 px-2 py-0.5 bg-emerald-100 text-emerald-600 text-[10px] rounded-full">
                                    {attendu.descripteurNom}
                                  </span>
                                  <SavoirOption
                                    label={attendu.description}
                                    onChange={() => handleAttenduChange(attendu)}
                                    checked={selectedAttendus.some(a => a.id === attendu.id)}
                                    type="attendu"
                                  />
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-gray-500 text-sm">Aucun attendu trouvé</p>
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      // Liste des descripteurs
                      descripteurs.map((desc) => (
                        <div
                          key={desc.id}
                          className="border-2 border-gray-200 rounded-xl overflow-hidden"
                        >
                          <div
                            onClick={() => setExpandedDescripteur(expandedDescripteur === desc.id ? null : desc.id)}
                            className={`
                              flex items-center justify-between p-3 xl:p-2 2xl:p-2.5 3xl:p-3 cursor-pointer transition-all
                              ${expandedDescripteur === desc.id
                                ? 'bg-blue-50 border-b border-blue-200'
                                : 'bg-gray-50 hover:bg-blue-50'
                              }
                            `}
                          >
                            <div className="flex items-center gap-2">
                              <BookOpen className="w-4 h-4 text-blue-500" />
                              <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-slate-700">{desc.nom}</span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expandedDescripteur === desc.id ? 'rotate-180' : ''}`} />
                          </div>

                          {expandedDescripteur === desc.id && (
                            <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 space-y-2 bg-white">
                              {activeTab === 'savoir' ? (
                                desc.savoirs_faires && desc.savoirs_faires.length > 0 ? (
                                  desc.savoirs_faires.map((savoir) => (
                                    <SavoirOption
                                      key={savoir.id}
                                      label={savoir.nom}
                                      onChange={() => handleSavoirChange(savoir)}
                                      checked={selectedSavoirs.some(s => s.id === savoir.id)}
                                      type="savoir"
                                    />
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-500 italic text-center py-2">
                                    Aucun savoir-faire
                                  </p>
                                )
                              ) : (
                                desc.attendus && desc.attendus.length > 0 ? (
                                  desc.attendus.map((attendu) => (
                                    <SavoirOption
                                      key={attendu.id}
                                      label={attendu.description}
                                      onChange={() => handleAttenduChange(attendu)}
                                      checked={selectedAttendus.some(a => a.id === attendu.id)}
                                      type="attendu"
                                    />
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-500 italic text-center py-2">
                                    Aucun attendu
                                  </p>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}

                    {descripteurs.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Aucun descripteur disponible pour ce thème</p>
                      </div>
                    )}
                  </div>

                  {/* Résumé des sélections */}
                  {(selectedSavoirs.length > 0 || selectedAttendus.length > 0) && (
                    <div className="mt-4 xl:mt-2 2xl:mt-3 3xl:mt-4 p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl text-white">
                      <div className="flex items-center gap-2 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                        <Check className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4" />
                        <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-bold">
                          {selectedSavoirs.length + selectedAttendus.length} élément(s) sélectionné(s)
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                        {selectedSavoirs.map((s) => (
                          <span key={s.id} className="px-2 py-0.5 bg-white/20 rounded text-xs">
                            {(s.nom || '').substring(0, 30)}...
                          </span>
                        ))}
                        {selectedAttendus.map((a) => (
                          <span key={a.id} className="px-2 py-0.5 bg-white/20 rounded text-xs">
                            {(a.description || '').substring(0, 30)}...
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Message si pas de thème sélectionné */}
          {!selectedTheme && (
            <div className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-blue-50 border border-blue-200 rounded-xl text-center">
              <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-blue-700">
                Sélectionnez d'abord un thème pour pouvoir ajouter des savoirs-faires et attendus.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 xl:p-3 2xl:p-4 3xl:p-6 bg-gray-50/80 flex-shrink-0">
          {/* Affichage des erreurs de validation */}
          {validationErrors.length > 0 && (
            <div className="mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4 p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-orange-50 border border-orange-200 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  {validationErrors.map((error, index) => (
                    <p key={index} className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-orange-700">
                      {error}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
            <button
              onClick={handleClose}
              className="px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 bg-orange-500 text-white rounded-xl font-medium xl:text-xs 2xl:text-sm 3xl:text-base hover:bg-orange-600 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid}
              className={`
                px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl font-medium xl:text-xs 2xl:text-sm 3xl:text-base transition-all duration-300
                ${isFormValid
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg hover:shadow-xl hover:scale-105'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Créer la séquence
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation de changement de thème (sélection à perdre) */}
      {themeEnAttente ? (
        <ConfirmChangeThemeModal
          ancienTheme={selectedTheme}
          nouveauTheme={themeEnAttente}
          nbSelections={selectedSavoirs.length + selectedAttendus.length}
          onConfirm={() => appliquerTheme(themeEnAttente)}
          onClose={() => setThemeEnAttente(null)}
        />
      ) : null}
    </div>
  );
}

export default AddSequenceNiveau;
